/**
 * Loop W2 — pure helpers for the signature-envelope wizard + tracker.
 * No React, no fetching: everything here is unit-testable.
 */

export const ENVELOPE_REPORT_TYPES = new Set(['pre_walk', 'post_walk', 'material_selection']);
export const DEFAULT_EXPIRY_DAYS = 14;
export const MAX_EXPIRY_DAYS = 60;
export const MAX_MESSAGE_LEN = 2000;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const isValidEmail = (v) => EMAIL_RE.test(String(v || '').trim());

/** Mirrors W1's fallback: explicit `signer_role`, else key containing "client". */
export function signerRoleOf(field) {
  if (field?.signer_role === 'client' || field?.signer_role === 'internal') return field.signer_role;
  return String(field?.key || '').includes('client') ? 'client' : 'internal';
}

export function signatureFieldsOf(template) {
  const out = [];
  for (const sec of template?.sections || []) {
    for (const f of sec.fields || []) {
      if (f.type !== 'signature') continue;
      out.push({ key: f.key || f.id, label: f.label || f.key, required: !!f.required, role: signerRoleOf(f) });
    }
  }
  return out;
}

export function unsignedFieldsOf(report) {
  const sigs = report?.signatures || {};
  return signatureFieldsOf(report?.template_snapshot).filter((f) => !sigs[f.key]?.data_url);
}

/** Open = W1's open_envelope_for: sent, or completed but not yet locked. */
export const isEnvelopeOpen = (env) =>
  !!env && (env.status === 'sent' || (env.status === 'completed' && !env.locked_at));

/** One wizard row per unsigned field; client rows prefill from job contacts, internal from team. */
export function buildInitialRows(fields, recipients) {
  const client = recipients?.customer_contacts?.[0];
  const team = recipients?.team_members?.[0];
  return fields.map((f) => {
    const pick = f.role === 'client' ? client : team;
    return { ...f, include: true, name: pick?.name || '', email: pick?.email || '' };
  });
}

export function moveRow(rows, from, to) {
  if (to < 0 || to >= rows.length || from === to) return rows;
  const next = [...rows];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

export const includedRows = (rows) => rows.filter((r) => r.include);

export function rowProblem(row) {
  if (!row.include) return '';
  if (!row.name.trim()) return 'Name is required';
  if (!isValidEmail(row.email)) return 'A valid email is required';
  return '';
}

export function expiryProblem(days) {
  const n = Number(days);
  if (!Number.isInteger(n) || n < 1 || n > MAX_EXPIRY_DAYS) return `Enter 1–${MAX_EXPIRY_DAYS} days`;
  return '';
}

export function buildEnvelopePayload({ rows, routing, expiryDays, message }) {
  return {
    signers: includedRows(rows).map((r, i) => ({
      field_key: r.key, name: r.name.trim(), email: r.email.trim(), order: i + 1,
    })),
    routing,
    message: (message || '').trim(),
    expires_in_days: Number(expiryDays),
  };
}

/** "When does this signer's email go out?" — the sender-facing sequencing explainer. */
export function scheduleLine(included, routing, idx) {
  if (routing === 'parallel' || idx === 0) return 'Emailed as soon as you send';
  const prev = included[idx - 1];
  return `Emailed automatically after ${prev.name || prev.label} signs`;
}

/**
 * Normalise a W1 error into what the wizard renders verbatim:
 * `{ status, code, message, missing: [{id,label}] }`.
 */
export function describeApiError(err) {
  const status = err?.response?.status || 0;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return {
      status,
      code: detail.code || '',
      message: detail.message || 'Request failed',
      missing: Array.isArray(detail.missing) ? detail.missing : [],
    };
  }
  return {
    status,
    code: '',
    message: (typeof detail === 'string' && detail) || err?.message || 'Request failed',
    missing: [],
  };
}

/** Plain-text form of any error detail (string or `{message}`), safe for toasts. */
export const errorText = (err, fallback = 'Request failed') => describeApiError(err).message || fallback;

export const STATUS_CHIP = {
  pending: { label: 'Waiting turn', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  sent: { label: 'Sent', cls: 'bg-sky-50 text-sky-700 border-sky-300' },
  viewed: { label: 'Viewed', cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  signed: { label: 'Signed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
};

/** The timestamp that goes with a signer's current chip. */
export function chipTimestamp(signer) {
  if (signer.status === 'signed') return signer.signed_at;
  if (signer.status === 'viewed') return signer.viewed_at;
  if (signer.status === 'sent') return signer.sent_at;
  return null;
}

/** Changes whenever the envelope moves in a way the report form must reflect. */
export function envelopeVersion(env) {
  if (!env) return '';
  const signed = (env.signers || []).filter((s) => s.status === 'signed').length;
  return `${env.id}:${env.status}:${signed}:${env.locked_at || ''}`;
}
